import { ContractPageData } from '@vy/api-definition';
import type { CustomFieldDefinition, CustomFieldValueWithDefinition } from '@vy/custom-fields-types';
import { isNotEmptyArray } from '@vy/utils';
import { useMemo } from 'react';
import { useContractBillingSchedule } from '../../../../../hooks/requests/use-contract-billing-schedule';
import { useDialog } from '../../../../../hooks/use-dialog';
import { ContractTabActions } from '../components/ContractTabActions';
import { ContractCustomFieldsSection } from '../components/ContractCustomFieldsSection/ContractCustomFieldsSection';
import { ContractRevenueScheduleSection } from '../components/ContractRevenueScheduleSection';
import { CreatePhaseDialog } from '../components/CreatePhaseDialog';
import { ProductProvisionSection } from '../components/ProductProvisionSection';
import { ContractBillingScheduleChartSection } from '../ContractBillingScheduleSection/ContractBillingScheduleSection';
import { useContractVersionData } from './use-contract-version-data';

type UseContractBillingSectionTabsParams = {
  contractPageData: ContractPageData;
  customFieldsWithDefinitions: CustomFieldValueWithDefinition[] | undefined;
  customFieldDefinitions: CustomFieldDefinition[] | undefined;
  startDate: Date;
  endDate?: Date;
};

export const useContractBillingSectionTabs = ({
  startDate,
  endDate,
  contractPageData,
  customFieldsWithDefinitions,
  customFieldDefinitions,
}: UseContractBillingSectionTabsParams) => {
  const { contract } = contractPageData;
  const { data: contractBillingScheduleData } = useContractBillingSchedule(contract.id, {
    suspense: true,
  });

  const { versionIdFromUrl, setVersionIdInUrl, contractVersions, isVersioned } = useContractVersionData({
    contractPageData,
  });

  const {
    isDialogOpen: isCreatePhaseDialogOpen,
    handleOpen: handleCreatePhaseDialogOpen,
    handleClose: handleCreatePhaseDialogClose,
  } = useDialog();

  const productProvisionActions = useMemo(
    () => (
      <ContractTabActions
        isVersioned={isVersioned}
        versionIdFromUrl={versionIdFromUrl}
        setVersionIdInUrl={setVersionIdInUrl}
        versions={contractVersions}
        contractId={contract.id}
        onAddPhase={handleCreatePhaseDialogOpen}
      />
    ),
    [
      isVersioned,
      versionIdFromUrl,
      setVersionIdInUrl,
      contractVersions,
      contract.id,
      handleCreatePhaseDialogOpen,
    ],
  );

  const tabs = useMemo(() => {
    const hasCustomFields =
      isNotEmptyArray(customFieldsWithDefinitions) || isNotEmptyArray(customFieldDefinitions);

    const tabsList = [
      {
        label: 'Product provision',
        render: () => (
          <ProductProvisionSection
            contractPageData={contractPageData}
            isVersioned={isVersioned}
            versionIdFromUrl={versionIdFromUrl}
            onAddPhase={handleCreatePhaseDialogOpen}
          />
        ),
        actions: productProvisionActions,
      },
      {
        label: 'Billing',
        render: () => (
          <ContractBillingScheduleChartSection
            contract={contract}
            startDate={startDate}
            endDate={endDate}
            billingScheduleData={contractBillingScheduleData?.invoices ?? []}
          />
        ),
      },
      {
        label: 'Revenue schedule',
        render: () => <ContractRevenueScheduleSection contractId={contract.id} />,
      },
    ];

    if (hasCustomFields) {
      tabsList.push({
        label: 'Custom',
        render: () => (
          <ContractCustomFieldsSection
            contractId={contract.id}
            customFieldsWithDefinitions={customFieldsWithDefinitions}
            customFieldDefinitions={customFieldDefinitions}
            integrationEntities={contract.integrationEntities ?? []}
          />
        ),
      });
    }

    return tabsList;
  }, [
    contract,
    customFieldDefinitions,
    customFieldsWithDefinitions,
    contractBillingScheduleData,
    contractPageData,
    endDate,
    isVersioned,
    startDate,
    handleCreatePhaseDialogOpen,
    productProvisionActions,
  ]);

  const phaseDialog = (
    <CreatePhaseDialog
      isDialogOpen={isCreatePhaseDialogOpen}
      handleClose={handleCreatePhaseDialogClose}
      contractStartDate={contract.startDate}
      contractEndDate={contract.endDate}
      contractId={contract.id}
    />
  );

  return { tabs, phaseDialog };
};
